import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import useCollection from './useCollection';
import { 
  FaTrashAlt, 
  FaEdit, 
  FaSave, 
  FaTimes, 
  FaComments, 
  FaExternalLinkAlt, 
  FaPaperPlane,
  FaUpload,
  FaLink
} from 'react-icons/fa';

const BUCKET_NAME = 'medias-troupe';

const LinkList = ({ collectionName, label }) => {
  const { profile, currentUser } = useAuth();
  const { items, loading, refetch } = useCollection(collectionName, 'created_at', true);

  // Type d'ajout : 'file' (upload) ou 'link' (lien externe)
  const [addType, setAddType] = useState('file');

  // États du formulaire
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [externalUrl, setExternalUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');

  // États d'édition
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editDescription, setEditDescription] = useState('');

  // États des commentaires
  const [comments, setComments] = useState([]);
  const [openCommentsId, setOpenCommentsId] = useState(null);
  const [newComment, setNewComment] = useState('');

  const isDirectorOrAdmin = profile?.is_admin || profile?.role === 'director';

  const fetchComments = useCallback(async () => {
    const { data } = await supabase
      .from('media_comments')
      .select('*')
      .eq('collection_name', collectionName)
      .order('created_at', { ascending: true });
    if (data) setComments(data);
  }, [collectionName]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const getMediaType = (url) => {
    if (!url) return 'other';
    const cleanUrl = url.split('?')[0].toLowerCase();
    if (cleanUrl.match(/\.(jpeg|jpg|gif|png|webp|svg)$/)) return 'image';
    if (cleanUrl.match(/\.(mp4|webm|ogg|mov|m4v)$/)) return 'video';
    return 'other';
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setMsg('');

    if (!title.trim()) {
      setMsg("Veuillez renseigner un titre.");
      return;
    }

    let finalUrl = '';

    setUploading(true);
    try {
      if (addType === 'file') {
        if (!file) {
          setMsg("Veuillez choisir un fichier à uploader.");
          setUploading(false);
          return;
        }

        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 9)}.${fileExt}`;
        const filePath = `${collectionName}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from(BUCKET_NAME)
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: publicData } = supabase.storage
          .from(BUCKET_NAME)
          .getPublicUrl(filePath);

        finalUrl = publicData.publicUrl;
      } else {
        if (!externalUrl.trim()) {
          setMsg("Veuillez renseigner un lien URL.");
          setUploading(false);
          return;
        }
        let formatted = externalUrl.trim();
        if (!formatted.startsWith('http://') && !formatted.startsWith('https://')) {
          formatted = 'https://' + formatted;
        }
        finalUrl = formatted;
      }

      const { error: insertError } = await supabase.from(collectionName).insert({
        title: title.trim(),
        url: finalUrl,
        description: description.trim(),
        author: profile?.name || 'Membre',
        created_by: currentUser?.id,
      });

      if (insertError) throw insertError;

      setTitle('');
      setDescription('');
      setFile(null);
      setExternalUrl('');
      setMsg("Élément publié avec succès ! 🚀");
      if (refetch) refetch();
    } catch (err) {
      setMsg("Erreur : " + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id, fileUrl, createdBy) => {
    const canDelete = isDirectorOrAdmin || createdBy === currentUser?.id;
    
    if (!canDelete) {
      alert("Vous n'avez pas la permission de supprimer cet élément.");
      return;
    }

    if (!window.confirm("Voulez-vous vraiment supprimer cet élément ?")) return;

    try {
      if (fileUrl && fileUrl.includes(BUCKET_NAME)) {
        const parts = fileUrl.split(`${BUCKET_NAME}/`);
        const filePathInBucket = parts.length > 1 ? decodeURIComponent(parts[1]) : null;

        if (filePathInBucket) {
          const { error: storageError } = await supabase.storage
            .from(BUCKET_NAME)
            .remove([filePathInBucket]);

          if (storageError) {
            console.error("Erreur suppression Storage :", storageError.message);
          }
        }
      }

      const { error: dbError } = await supabase
        .from(collectionName)
        .delete()
        .eq('id', id);

      if (dbError) throw dbError;

      if (refetch) refetch();
    } catch (err) {
      alert("Erreur lors de la suppression : " + err.message);
    }
  };

  const handleStartEdit = (item) => {
    setEditingId(item.id);
    setEditTitle(item.title || '');
    setEditUrl(item.url || '');
    setEditDescription(item.description || '');
  };

  const handleSaveEdit = async (id) => {
    try {
      let formattedUrl = editUrl.trim();
      if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
        formattedUrl = 'https://' + formattedUrl;
      }

      const { error } = await supabase
        .from(collectionName)
        .update({
          title: editTitle.trim(),
          url: formattedUrl,
          description: editDescription.trim(),
        })
        .eq('id', id);

      if (error) throw error;

      setEditingId(null);
      if (refetch) refetch();
    } catch (err) {
      alert("Erreur lors de la modification : " + err.message);
    }
  };

  const toggleComments = (id) => {
    setOpenCommentsId(openCommentsId === id ? null : id);
  };

  const handleAddComment = async (mediaId) => {
    if (!newComment.trim()) return;

    const { error } = await supabase.from('media_comments').insert({
      collection_name: collectionName,
      media_id: mediaId,
      user_id: currentUser?.id,
      author: profile?.name || 'Membre',
      content: newComment.trim(),
    });

    if (!error) {
      setNewComment('');
      fetchComments();
    } else {
      alert("Erreur lors de l'ajout du commentaire : " + error.message);
    }
  };

  const handleDeleteComment = async (commentId, commentUserId) => {
    const canDeleteComment = isDirectorOrAdmin || commentUserId === currentUser?.id;
    if (!canDeleteComment) return;

    const { error } = await supabase.from('media_comments').delete().eq('id', commentId);
    if (!error) fetchComments();
  };

  return (
    <div className="membres-section">
      <h3>{label}</h3>

      {/* SÉLECTEUR DU TYPE D'AJOUT */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
        <button
          type="button"
          onClick={() => setAddType('file')}
          style={{
            flex: 1,
            padding: '10px',
            borderRadius: '6px',
            border: 'none',
            backgroundColor: addType === 'file' ? '#007bff' : 'rgba(255,255,255,0.1)',
            color: '#fff',
            cursor: 'pointer',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <FaUpload /> Uploader un fichier
        </button>
        <button
          type="button"
          onClick={() => setAddType('link')}
          style={{
            flex: 1,
            padding: '10px',
            borderRadius: '6px',
            border: 'none',
            backgroundColor: addType === 'link' ? '#007bff' : 'rgba(255,255,255,0.1)',
            color: '#fff',
            cursor: 'pointer',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <FaLink /> Lien externe (YouTube, Drive...)
        </button>
      </div>

      {/* FORMULAIRE D'AJOUT */}
      <form onSubmit={handleAdd} className="membres-form" style={{ marginBottom: '25px' }}>
        <input
          type="text"
          placeholder="Titre (ex: Filage complet Acte 1, Photos répétition...)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          style={{ width: '100%', marginBottom: '10px', padding: '10px', borderRadius: '6px' }}
        />

        {addType === 'file' ? (
          <div style={{ marginBottom: '10px', padding: '12px', border: '1px dashed rgba(255,255,255,0.3)', borderRadius: '6px' }}>
            <label style={{ display: 'block', marginBottom: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
              <FaUpload style={{ marginRight: '8px' }} /> 
              {file ? `Sélectionné : ${file.name}` : "Choisir un fichier vidéo ou photo..."}
            </label>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              accept="video/*,image/*"
              style={{ width: '100%' }}
            />
          </div>
        ) : (
          <input
            type="url"
            placeholder="Lien (YouTube non répertorié, Google Drive, Dropbox...)"
            value={externalUrl}
            onChange={(e) => setExternalUrl(e.target.value)}
            style={{ width: '100%', marginBottom: '10px', padding: '10px', borderRadius: '6px' }}
          />
        )}

        <textarea
          placeholder="Description ou remarques (optionnel)..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows="2"
          style={{ width: '100%', marginBottom: '10px', padding: '10px', borderRadius: '6px' }}
        />

        {msg && <p style={{ color: msg.includes('succès') ? '#28a745' : '#dc3545', marginBottom: '10px' }}>{msg}</p>}

        <button type="submit" className="membres-btn" disabled={uploading}>
          {addType === 'file' ? <FaUpload /> : <FaLink />}
          {uploading ? " Publication en cours..." : " Publier"}
        </button>
      </form>

      {/* LISTE DES MÉDIAS */}
      {loading ? (
        <p>Chargement...</p>
      ) : items.length === 0 ? (
        <p style={{ fontStyle: 'italic', opacity: 0.7 }}>Aucun fichier partagé pour le moment.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {items.map((item) => {
            const itemComments = comments.filter((c) => c.media_id === item.id);
            const canManage = isDirectorOrAdmin || item.created_by === currentUser?.id;
            const mediaType = getMediaType(item.url);

            return (
              <div key={item.id} className="info-box" style={{ borderRadius: '8px', padding: '15px' }}>
                {editingId === item.id ? (
                  <div>
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      placeholder="Titre"
                      style={{ width: '100%', marginBottom: '8px', padding: '8px' }}
                    />
                    <input
                      type="url"
                      value={editUrl}
                      onChange={(e) => setEditUrl(e.target.value)}
                      placeholder="URL / Lien"
                      style={{ width: '100%', marginBottom: '8px', padding: '8px' }}
                    />
                    <textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Description"
                      rows="2"
                      style={{ width: '100%', marginBottom: '8px', padding: '8px' }}
                    />
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button type="button" className="membres-btn" onClick={() => handleSaveEdit(item.id)}>
                        <FaSave /> Enregistrer
                      </button>
                      <button type="button" className="membres-btn" style={{ backgroundColor: '#6c757d' }} onClick={() => setEditingId(null)}>
                        <FaTimes /> Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                      <h4 style={{ margin: 0, fontSize: '1.2rem' }}>{item.title}</h4>

                      {canManage && (
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button
                            type="button"
                            onClick={() => handleStartEdit(item)}
                            style={{ background: 'none', border: 'none', color: '#17a2b8', cursor: 'pointer' }}
                            title="Modifier"
                          >
                            <FaEdit />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(item.id, item.url, item.created_by)}
                            style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer' }}
                            title="Supprimer"
                          >
                            <FaTrashAlt />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* APERÇU OU LIEN */}
                    <div style={{ margin: '12px 0', textAlign: 'center', background: '#000', borderRadius: '8px', overflow: 'hidden' }}>
                      {mediaType === 'image' && (
                        <a href={item.url} target="_blank" rel="noopener noreferrer">
                          <img 
                            src={item.url} 
                            alt={item.title} 
                            style={{ maxWidth: '100%', maxHeight: '450px', objectFit: 'contain', display: 'block', margin: '0 auto' }} 
                          />
                        </a>
                      )}

                      {mediaType === 'video' && (
                        <video controls style={{ width: '100%', maxHeight: '450px', display: 'block' }}>
                          <source src={item.url} />
                          Votre navigateur ne prend pas en charge la lecture vidéo.
                        </video>
                      )}

                      {mediaType === 'other' && (
                        <div style={{ padding: '20px' }}>
                          <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', fontWeight: 'bold', fontSize: '1.1rem' }}>
                            Ouvrir le lien externe <FaExternalLinkAlt style={{ fontSize: '0.8rem', marginLeft: '5px' }} />
                          </a>
                        </div>
                      )}
                    </div>

                    {item.description && (
                      <p style={{ margin: '8px 0', whiteSpace: 'pre-wrap', opacity: 0.9 }}>
                        {item.description}
                      </p>
                    )}

                    <small style={{ opacity: 0.7, display: 'block', marginTop: '6px' }}>
                      Ajouté par {item.author || 'un membre'} le {new Date(item.created_at).toLocaleDateString('fr-FR')}
                    </small>

                    {/* COMMENTAIRES */}
                    <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                      <button
                        type="button"
                        onClick={() => toggleComments(item.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#f1c40f',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '0.9rem',
                        }}
                      >
                        <FaComments />
                        {itemComments.length === 0
                          ? 'Ajouter un commentaire'
                          : `${itemComments.length} commentaire(s)`}
                      </button>

                      {openCommentsId === item.id && (
                        <div style={{ marginTop: '12px', background: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '6px' }}>
                          {itemComments.map((c) => (
                            <div
                              key={c.id}
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                borderBottom: '1px solid rgba(255,255,255,0.05)',
                                paddingBottom: '6px',
                                marginBottom: '6px',
                              }}
                            >
                              <div>
                                <strong>{c.author} :</strong> {c.content}
                                <small style={{ display: 'block', opacity: 0.5, fontSize: '0.75rem' }}>
                                  {new Date(c.created_at).toLocaleDateString('fr-FR')}
                                </small>
                              </div>
                              {(c.user_id === currentUser?.id || isDirectorOrAdmin) && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteComment(c.id, c.user_id)}
                                  style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer' }}
                                  title="Supprimer"
                                >
                                  <FaTrashAlt style={{ fontSize: '0.75rem' }} />
                                </button>
                              )}
                            </div>
                          ))}

                          <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                            <input
                              type="text"
                              placeholder="Écrire un commentaire..."
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleAddComment(item.id)}
                              style={{ flex: 1, padding: '8px', borderRadius: '4px' }}
                            />
                            <button
                              type="button"
                              className="membres-btn"
                              onClick={() => handleAddComment(item.id)}
                              style={{ padding: '8px 12px' }}
                            >
                              <FaPaperPlane />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LinkList;